import { CommonModule } from '@angular/common';
import { Component, OnInit, computed, effect, input, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { FormArray, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { debounceTime, distinctUntilChanged, take } from 'rxjs/operators';
import { TRANSLOCO_SCOPE, TranslocoModule } from '@jsverse/transloco';
import {
  QaiAlertComponent,
  QaiFieldComponent,
  QaiInputComponent,
  QaiLinkButtonComponent,
  QaiLoadingComponent,
  QaiPasteCleanDirective,
  QaiTextareaComponent,
  QaiTrimLeadingDirective,
  TextareaAutosizeDirective,
} from '@qai/ui';
import { TabsInnerWrapComponent } from '../../components/tabs-inner-wrap/tabs-inner-wrap.component';
import { WhatUsersSeeCardComponent } from './what-users-see-card/what-users-see-card.component';
import { AuthorService } from 'src/app/services/self-serve/author.service';
import { AuthorReactiveFormService } from '../../services/author-reactive-form.service';
import { PreviewSimulationDetailsModalComponent } from '../../components/modals/preview-simulation-details/preview-simulation-modal.component';
import { CallRefinementPollingService } from 'src/app/services/self-serve/call-refinement-polling.service';
import { CallRefinementStatus } from 'src/app/utils/enums/self-serve/call-refinement-status.enum';
import { SimulationConstants } from 'src/app/utils/constants/self-serve/simulation.constants';
import { ISimulationDetail } from 'src/app/interfaces/self-serve/isimulation-details-information';
import { IFetchSimulationDetails } from 'src/app/interfaces/self-serve/responses/ifetch-simulation-details';
import { ISimulationDetails } from 'src/app/interfaces/self-serve/responses/isimulation-details';
import { ISimulationUpdate } from 'src/app/interfaces/self-serve/requests/isimulation-update';
import { AmplitudeService } from 'src/app/services/external-tools/amplitude.service';
import { AmplitudeTrackingEvents } from 'src/app/utils/enums/amplitude/amplitudeTrackingEvents.enum';

const OVERVIEW_TAB_TITLE = 'Simulation Overview';
const PERSONA_TAB_TITLE = 'About the Persona';
const SCORING_TAB_TITLE = 'Scoring Criteria';
const ADDITIONAL_SECTION_TITLE = 'Additional Section';
const DESCRIPTION_SECTION = 'Description';

@Component({
  selector: 'tab-what-users-see',
  standalone: true,
  templateUrl: './what-users-see-details.component.html',
  styleUrls: ['./what-users-see-details.component.scss'],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TabsInnerWrapComponent,
    WhatUsersSeeCardComponent,
    PreviewSimulationDetailsModalComponent,
    QaiAlertComponent,
    QaiFieldComponent,
    QaiInputComponent,
    QaiLinkButtonComponent,
    QaiTextareaComponent,
    QaiLoadingComponent,
    TextareaAutosizeDirective,
    QaiTrimLeadingDirective,
    QaiPasteCleanDirective,
    TranslocoModule,
  ],
  providers: [{ provide: TRANSLOCO_SCOPE, useValue: 'simulationBuilder' }],
})
export class WhatUsersSeeDetailsComponent implements OnInit {
  /** Knowledge Check (GUIDED) shows these fields read-only. */
  isKnowledgeCheck = input(false);

  loading = signal(false);
  loadError = signal(false);

  /** True when callDetailsAuthorOverride is false — warns the author that
   * saving an edit here will opt this content out of future refinements. */
  showAuthorOverrideWarning = signal(false);

  openPreviewModal = false;
  previewTabs: { label: string; content: string }[] = [];

  descriptionControl = new FormControl('');
  overviewControl = new FormControl('');
  personaControl = new FormControl('');
  scoringControl = new FormControl('');
  additionalSectionTitleControl = new FormControl('');
  additionalSectionInfoControl = new FormControl('');

  private callUuid = '';
  private moduleUuid = '';
  private simulationDetailsInfoGroup: FormGroup | undefined;
  /** The shared `general` FormGroup — read from here for name/moduleUuid/
   * language when building the description autosave payload, since this
   * component only owns descriptionControl, not those sibling controls. */
  private generalGroup: FormGroup | undefined;

  /** Reactive read of the shared refinement job status — repopulates the
   * call details from the server once a refinement completes, unless the
   * author has manually overridden them (callDetailsAuthorOverride). */
  private readonly pollingStatus = computed(() =>
    this.callRefinementPollingService.getStatus(this.callUuid),
  );

  /** Snapshot of each tracked field's value, used to detect which section an
   * author actually edited when simulationDetailsInfoGroup.valueChanges fires
   * for the group as a whole, for self_authoring_user_view_edited tracking. */
  private previousDetailValues: Record<string, string> = {};

  constructor(
    private authorService: AuthorService,
    private authorReactiveFormService: AuthorReactiveFormService,
    private route: ActivatedRoute,
    private callRefinementPollingService: CallRefinementPollingService,
    private amplitudeService: AmplitudeService
  ) {
    effect(() => {
      if (this.pollingStatus() === CallRefinementStatus.Updated) {
        const authorOverride =
          this.simulationDetailsInfoGroup?.get('callDetailsAuthorOverride')?.value;
        if (!authorOverride) {
          this.fetchSimulationDetails();
        }
      }
    });
  }

  ngOnInit(): void {
    this.callUuid = this.route.snapshot.paramMap.get('uuid') || '';
    this.moduleUuid = this.route.snapshot.queryParamMap.get('moduleUuid') || '';

    if (!this.callUuid) return;

    try {
      this.authorReactiveFormService
        .getForm(this.callUuid)
        .pipe(take(1))
        .subscribe((form) => {
          this.simulationDetailsInfoGroup = form.get(
            SimulationConstants.SIMULATION_DETAILS_INFO_PATH
          ) as FormGroup;

          const tabs = this.simulationDetailsInfoGroup.get(
            SimulationConstants.SIMULATION_DETAILS_TABS_SECTION
          ) as FormArray;

          this.overviewControl = this.findTabTextControl(tabs, OVERVIEW_TAB_TITLE);
          this.personaControl = this.findTabTextControl(tabs, PERSONA_TAB_TITLE);
          this.scoringControl = this.findTabTextControl(tabs, SCORING_TAB_TITLE);
          this.additionalSectionTitleControl = this.simulationDetailsInfoGroup.get(
            'additionalSectionTitle'
          ) as FormControl;
          this.additionalSectionInfoControl = this.simulationDetailsInfoGroup.get(
            'additionalSectionInfo'
          ) as FormControl;

          this.generalGroup = form.get(SimulationConstants.GENERAL_SECTION) as FormGroup;
          this.descriptionControl = this.generalGroup.get('simulationDescription') as FormControl;

          if (this.isKnowledgeCheck()) {
            this.useReadOnlyControls();
            return;
          }

          this.refreshAuthorOverrideWarning();

          this.setupAutoSave();
          this.listenToDescriptionChanges();
        });
    } catch (err) {
      console.error('Simulation form is not initialized yet:', err);
      this.loadError.set(true);
    }
  }

  previewDetailsModal(): void {
    const tabs = this.simulationDetailsInfoGroup?.get(
      SimulationConstants.SIMULATION_DETAILS_TABS_SECTION
    ) as FormArray;

    const detailTabs: { label: string; content: string }[] = (tabs?.getRawValue() ?? []).map(
      (tab: ISimulationDetail) => ({ label: tab.tabTitle, content: tab.tabText })
    );

    const additionalSectionTitle = this.additionalSectionTitleControl.value;
    const additionalSectionInfo = this.additionalSectionInfoControl.value;
    if (additionalSectionTitle && additionalSectionInfo) {
      detailTabs.push({ label: additionalSectionTitle, content: additionalSectionInfo });
    }

    this.previewTabs = detailTabs;
    this.openPreviewModal = true;
  }

  handlePreviewModalClose(): void {
    this.openPreviewModal = false;
  }

  // Disabled copies, so the shared form controls (e.g. description on the General tab) stay
  // editable and no autosave is wired up.
  private useReadOnlyControls(): void {
    const readOnlyCopy = (control: FormControl) =>
      new FormControl({ value: control.value, disabled: true });

    this.descriptionControl = readOnlyCopy(this.descriptionControl);
    this.overviewControl = readOnlyCopy(this.overviewControl);
    this.personaControl = readOnlyCopy(this.personaControl);
    this.scoringControl = readOnlyCopy(this.scoringControl);
    this.additionalSectionTitleControl = readOnlyCopy(this.additionalSectionTitleControl);
    this.additionalSectionInfoControl = readOnlyCopy(this.additionalSectionInfoControl);
  }

  private refreshAuthorOverrideWarning(): void {
    const authorOverride = this.simulationDetailsInfoGroup?.get('callDetailsAuthorOverride')?.value;
    this.showAuthorOverrideWarning.set(!authorOverride);
  }

  private findTabTextControl(tabs: FormArray, tabTitle: string): FormControl {
    const group = tabs?.controls.find(
      (control) => control.get('tabTitle')?.value === tabTitle
    ) as FormGroup | undefined;
    return (group?.get('tabText') as FormControl) ?? new FormControl('');
  }

  private setupAutoSave(): void {
    if (!this.simulationDetailsInfoGroup) return;

    this.captureDetailSnapshot();

    this.simulationDetailsInfoGroup.valueChanges
      .pipe(
        debounceTime(SimulationConstants.AUTOSAVE_GENERIC_DEBOUNCE),
        distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b))
      )
      .subscribe(() => {
        this.trackEditedSections();
        this.saveCallDetails();
      });
  }

  private captureDetailSnapshot(): void {
    this.previousDetailValues = this.getCurrentDetailValues();
  }

  private getCurrentDetailValues(): Record<string, string> {
    return {
      [OVERVIEW_TAB_TITLE]: this.overviewControl.value || '',
      [PERSONA_TAB_TITLE]: this.personaControl.value || '',
      [SCORING_TAB_TITLE]: this.scoringControl.value || '',
      [ADDITIONAL_SECTION_TITLE]: `${this.additionalSectionTitleControl.value || ''}|${this.additionalSectionInfoControl.value || ''}`,
    };
  }

  private trackEditedSections(): void {
    const current = this.getCurrentDetailValues();

    Object.entries(current).forEach(([section, value]) => {
      if (this.previousDetailValues[section] !== value) {
        this.amplitudeService.trackEvent(AmplitudeTrackingEvents.SELF_AUTHORING_USER_VIEW_EDITED, {
          call_uuid: this.callUuid,
          section,
        });
      }
    });

    this.previousDetailValues = current;
  }

  private listenToDescriptionChanges(): void {
    this.descriptionControl.valueChanges
      .pipe(
        debounceTime(SimulationConstants.AUTOSAVE_GENERIC_DEBOUNCE),
        distinctUntilChanged()
      )
      .subscribe(() => {
        const generalValue = this.generalGroup?.value;

        const payload: ISimulationUpdate = {
          uuid: this.callUuid,
          name: (generalValue?.simulationName || '').trim(),
          description: this.descriptionControl.value,
          moduleUuid: generalValue?.moduleUuid || '',
          existingModuleUuid: generalValue?.moduleUuid || '',
          language: generalValue?.language,
        };

        this.authorService.updateSimulation(payload).subscribe({
          next: () => {
            this.authorReactiveFormService.patchFormSilently(
              this.callUuid,
              SimulationConstants.SIMULATION_DETAILS_INFO_PATH,
              { callDetailsAuthorOverride: true }
            );
            this.refreshAuthorOverrideWarning();
            this.amplitudeService.trackEvent(AmplitudeTrackingEvents.SELF_AUTHORING_USER_VIEW_EDITED, {
              call_uuid: this.callUuid,
              section: DESCRIPTION_SECTION,
            });
          },
          error: (err) => console.error('Failed to update simulation description:', err),
        });
      });
  }

  private saveCallDetails(): void {
    const tabs = this.simulationDetailsInfoGroup?.get(
      SimulationConstants.SIMULATION_DETAILS_TABS_SECTION
    ) as FormArray;
    if (!tabs) return;

    const detailTabs: ISimulationDetail[] = tabs.getRawValue().map(
      (tab: ISimulationDetail, idx: number) => ({
        tabSequence: idx + 1,
        tabTitle: tab.tabTitle,
        tabText: tab.tabText,
        uuid: tab.uuid || null,
      })
    );

    const additionalSectionTitle = this.additionalSectionTitleControl.value || '';
    const additionalSectionInfo = this.additionalSectionInfoControl.value || '';
    const additionalSectionUuid = this.simulationDetailsInfoGroup?.get(
      'additionalSectionUuid'
    )?.value;

    const hasAdditionalContent = !!(
      additionalSectionTitle.trim() || additionalSectionInfo.trim()
    );
    const additionalSection: ISimulationDetail = {
      tabSequence: detailTabs.length + 1,
      tabTitle: additionalSectionTitle,
      tabText: additionalSectionInfo,
      uuid: additionalSectionUuid || null,
    };

    const callDetails = hasAdditionalContent
      ? [...detailTabs, additionalSection]
      : detailTabs;

    const allTabsExist = detailTabs.every((detail) => !!detail.uuid);
    const additionalIsNew = hasAdditionalContent && !additionalSectionUuid;
    const isAlreadyCreated = allTabsExist && !additionalIsNew;

    if (!isAlreadyCreated) {
      const isPartialCreate = allTabsExist && additionalIsNew;
      const createPayload = isPartialCreate
        ? [callDetails[callDetails.length - 1]]
        : callDetails;
      this.createCallDetails(createPayload, isPartialCreate);
    } else {
      this.updateCallDetails(callDetails);
    }
  }

  private createCallDetails(callDetails: ISimulationDetail[], isPartialCreate: boolean): void {
    this.authorService.createSimulationDetails(this.callUuid, callDetails).subscribe({
      next: (res: ISimulationDetails) => {
        if (isPartialCreate) {
          const newRecord = res.callDetails?.[0];
          if (newRecord?.uuid) {
            this.authorReactiveFormService.patchFormSilently(
              this.callUuid,
              SimulationConstants.SIMULATION_DETAILS_INFO_PATH,
              { additionalSectionUuid: newRecord.uuid, callDetailsAuthorOverride: true }
            );
          }
        } else {
          const normalized = this.authorReactiveFormService.normalizeCallDetailsToForm(
            res.callDetails
          );
          this.authorReactiveFormService.patchFormSilently(
            this.callUuid,
            SimulationConstants.SIMULATION_DETAILS_INFO_PATH,
            { ...normalized, callDetailsAuthorOverride: true }
          );
        }
        this.refreshAuthorOverrideWarning();
      },
      error: (err) => console.error('Failed to create call details:', err),
    });
  }

  private updateCallDetails(callDetails: ISimulationDetail[]): void {
    this.authorService.updateSimulationDetails(callDetails, this.callUuid).subscribe({
      next: () => {
        this.authorReactiveFormService.patchFormSilently(
          this.callUuid,
          SimulationConstants.SIMULATION_DETAILS_INFO_PATH,
          { callDetailsAuthorOverride: true }
        );
        this.refreshAuthorOverrideWarning();
      },
      error: (err) => console.error('Failed to update call details:', err),
    });
  }

  private fetchSimulationDetails(): void {
    if (!this.callUuid) return;

    this.loading.set(true);
    this.loadError.set(false);

    this.authorService.getSimulationDetails(this.callUuid, this.moduleUuid).subscribe({
      next: (response: IFetchSimulationDetails) => {
        const callDetails = response.callDetailsList?.callDetails || [];
        const normalized = this.authorReactiveFormService.normalizeCallDetailsToForm(callDetails);

        this.authorReactiveFormService.patchFormSilently(
          this.callUuid,
          SimulationConstants.SIMULATION_DETAILS_INFO_PATH,
          {
            simulationDetailtabs: normalized.simulationDetailtabs,
            callDetailsAuthorOverride: response.callDetailsAuthorOverride ?? false,
          }
        );

        this.authorReactiveFormService.patchFormSilently(this.callUuid, 'general', {
          simulationDescription: response.description?.trim() || '',
        });
        this.refreshAuthorOverrideWarning();
        this.loading.set(false);
      },
      error: (err) => {
        console.error('Failed to fetch simulation details:', err);
        this.loadError.set(true);
        this.loading.set(false);
      },
    });
  }
}
